import { isValidGuid } from 'infrastructure/functions';
import { useUserStatus } from 'infrastructure/hooks/use-user-status';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';

type TabState = {
  tabId: string;
  route: string;
  isActive: boolean;
  timestamp: number;
};

const TAB_TIMEOUT_MS = 10_000;
const HEARTBEAT_INTERVAL_MS = TAB_TIMEOUT_MS / 2;

const CrossTabStatusProvider = () => {
  const location = useLocation();
  const { loading, statuses, currentStatus, setStatus } = useUserStatus();

  const inChartStatus = useMemo(
    () => statuses.find((el) => el.label.trim().toLowerCase() === 'in chart'),
    [statuses],
  );
  const availableStatus = useMemo(
    () => statuses.find((el) => el.label.trim().toLowerCase() === 'available'),
    [statuses],
  );
  const offlineStatus = useMemo(
    () => statuses.find((el) => el.label.trim().toLowerCase() === 'offline'),
    [statuses],
  );

  const isInChartStatus = currentStatus === inChartStatus?.value;
  const isAvailableStatus = currentStatus === availableStatus?.value;
  const isOfflineStatus = currentStatus === offlineStatus?.value;

  const stateRef = useRef({
    loading,
    isInChartStatus,
    isAvailableStatus,
    isOfflineStatus,
    inChartStatus,
    availableStatus,
    setStatus,
  });
  stateRef.current = {
    loading,
    isInChartStatus,
    isAvailableStatus,
    isOfflineStatus,
    inChartStatus,
    availableStatus,
    setStatus,
  };

  const locationRef = useRef(location.pathname);
  locationRef.current = location.pathname;

  const TAB_ID = useRef(crypto.randomUUID());
  const channel = useRef<BroadcastChannel | null>(null);
  const tabs = useRef<Map<string, TabState>>(new Map());
  const isHydrated = useRef(false);
  const isActiveRef = useRef(document.hasFocus());

  const evaluateStatus = useCallback(() => {
    const now = Date.now();

    // Always refresh own tab state before evaluating to prevent own entry
    // from expiring when inactive (e.g. another tab triggers evaluateStatus
    // after TAB_TIMEOUT_MS passes without any navigation/focus on this tab).
    tabs.current.set(TAB_ID.current, {
      tabId: TAB_ID.current,
      route: locationRef.current,
      isActive: isActiveRef.current,
      timestamp: now,
    });

    tabs.current.forEach((tab, key) => {
      if (now - tab.timestamp > TAB_TIMEOUT_MS) {
        tabs.current.delete(key);
      }
    });

    const hasInChartTab = Array.from(tabs.current.values()).some((tab) => {
      const path = tab.route.split('/');
      return path.length === 3 && path[1] === 'patient' && isValidGuid(path[2]);
    });

    if (stateRef.current.loading) return;

    if (
      hasInChartTab &&
      !stateRef.current.isInChartStatus &&
      stateRef.current.inChartStatus
    ) {
      stateRef.current.setStatus(stateRef.current.inChartStatus.record);
    } else if (
      ((!hasInChartTab && stateRef.current.isInChartStatus) ||
        stateRef.current.isOfflineStatus) &&
      !stateRef.current.isAvailableStatus &&
      stateRef.current.availableStatus
    ) {
      stateRef.current.setStatus(stateRef.current.availableStatus.record);
    }
  }, []);

  const sendState = useCallback(() => {
    if (!channel.current) return;

    const payload: TabState = {
      tabId: TAB_ID.current,
      route: locationRef.current,
      isActive: isActiveRef.current,
      timestamp: Date.now(),
    };

    tabs.current.set(TAB_ID.current, payload);
    channel.current.postMessage({ type: 'STATE_UPDATE', payload });

    if (isHydrated.current) {
      evaluateStatus();
    }
  }, [evaluateStatus]);

  useEffect(() => {
    channel.current = new BroadcastChannel('app_tabs');

    channel.current.onmessage = ({ data: { type, payload } }) => {
      switch (type) {
        case 'REQUEST_STATE':
          sendState();
          break;
        case 'STATE_UPDATE':
          tabs.current.set(payload.tabId, payload);
          if (isHydrated.current) evaluateStatus();
          break;
        case 'TAB_CLOSED':
          tabs.current.delete(payload.tabId);
          evaluateStatus();
          break;
        default:
          break;
      }
    };

    channel.current.postMessage({ type: 'REQUEST_STATE' });

    const hydrationTimeout = setTimeout(() => {
      isHydrated.current = true;
      sendState();
    }, 300);

    const heartbeat = setInterval(() => {
      sendState();
    }, HEARTBEAT_INTERVAL_MS);

    const notifyTabClosed = () => {
      channel.current?.postMessage({
        type: 'TAB_CLOSED',
        payload: { tabId: TAB_ID.current },
      });
    };

    window.addEventListener('pagehide', notifyTabClosed);

    return () => {
      clearTimeout(hydrationTimeout);
      clearInterval(heartbeat);
      window.removeEventListener('pagehide', notifyTabClosed);
      notifyTabClosed();
      channel.current?.close();
    };
  }, []);

  useEffect(() => {
    const onFocus = () => {
      isActiveRef.current = true;
      sendState();
    };
    const onBlur = () => {
      isActiveRef.current = false;
      sendState();
    };

    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);

    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
    };
  }, [sendState]);

  useEffect(() => {
    sendState();
  }, [location.pathname]);

  return null;
};

export default CrossTabStatusProvider;
